import { z } from 'zod';
import { uuidSchema } from './common.schema';
import { PaginationQuerySchema, PaginationMetaSchema } from './pagination.schema';

// ═══════════════════════════════════════════════════════════════════════════════
// MILK LOG  (backed by patient_milk_log table)
// ═══════════════════════════════════════════════════════════════════════════════

// ─── Create ────────────────────────────────────────────────────────────────────

export const CreateMilkLogRequestSchema = z.object({
  patient_id:           uuidSchema,
  hospital_id:          uuidSchema,
  encounter_id:         uuidSchema.optional().nullable(),
  daily_volume:         z.number().positive('Daily volume must be positive'),
  preferred_milk_type:  z.string().max(30),
  feeding_frequency:    z.number().int().positive('Feeding frequency must be a positive integer'),
  volume_per_feed:      z.number().positive('Volume per feed must be positive'),
  fortifier:            z.string().max(200).optional().nullable(),
  target_calories:      z.number().positive().optional().nullable(),
  effective_date:       z.coerce.date(),
  special_instructions: z.string().optional().nullable(),
});
export type CreateMilkLogRequest = z.infer<typeof CreateMilkLogRequestSchema>;

// ─── Update ────────────────────────────────────────────────────────────────────

export const UpdateMilkLogRequestSchema = z.object({
  daily_volume:         z.number().positive().optional(),
  preferred_milk_type:  z.string().max(30).optional(),
  feeding_frequency:    z.number().int().positive().optional(),
  volume_per_feed:      z.number().positive().optional(),
  fortifier:            z.string().max(200).nullable().optional(),
  target_calories:      z.number().positive().nullable().optional(),
  effective_date:       z.coerce.date().optional(),
  special_instructions: z.string().nullable().optional(),
  is_active:            z.boolean().optional(),
  recorded_by:          uuidSchema.optional(),
}).refine((d) => Object.keys(d).length > 0, {
  message: 'At least one field must be provided for update',
});
export type UpdateMilkLogRequest = z.infer<typeof UpdateMilkLogRequestSchema>;

// ─── List query ────────────────────────────────────────────────────────────────

export const MilkLogListQuerySchema = PaginationQuerySchema.extend({
  is_active:   z.enum(['true', 'false']).optional(),
});
export type MilkLogListQuery = z.infer<typeof MilkLogListQuerySchema>;

// ─── Response item ─────────────────────────────────────────────────────────────

export const MilkLogItemSchema = z.object({
  milk_log_id:          uuidSchema,
  patient_id:           uuidSchema,
  hospital_id:          uuidSchema,
  encounter_id:         uuidSchema.nullable(),
  daily_volume:         z.number(),
  preferred_milk_type:  z.string(),
  feeding_frequency:    z.number().int(),
  volume_per_feed:      z.number(),
  fortifier:            z.string().nullable(),
  target_calories:      z.number().nullable(),
  effective_date:       z.preprocess((v) => v instanceof Date ? v.toISOString().slice(0, 10) : typeof v === 'string' ? v.slice(0, 10) : v, z.iso.date()),
  special_instructions: z.string().nullable(),
  recorded_by:          uuidSchema,
  recorded_by_name:     z.string().nullable().optional(),
  is_active:            z.boolean(),
  created_at:           z.coerce.date(),
  updated_at:           z.coerce.date(),
});
export type MilkLogItem = z.infer<typeof MilkLogItemSchema>;

// ─── List response ─────────────────────────────────────────────────────────────

export const GetMilkLogListResponseSchema = z.object({
  items:      z.array(MilkLogItemSchema),
  pagination: PaginationMetaSchema,
});
export type GetMilkLogListResponse = z.infer<typeof GetMilkLogListResponseSchema>;

// ─── Create request ────────────────────────────────────────────────────────────

export const CreateMedicalHistoryRequestSchema = z.object({
  patient_id:   uuidSchema,
  hospital_id:  uuidSchema,
  encounter_id: uuidSchema.optional().nullable().describe('Encounter UUID (optional)'),
  category:     z.string().optional().describe('Category of the medical history entry'),
  narrative:    z.string().min(1, 'Narrative is required').max(5000),
});
export type CreateMedicalHistoryRequest = z.infer<typeof CreateMedicalHistoryRequestSchema>;

// ─── Update request ────────────────────────────────────────────────────────────

export const UpdateMedicalHistoryRequestSchema = z.object({
  narrative: z.string().min(1).max(5000).optional(),
  is_active: z.boolean().optional(),
}).refine((d) => Object.keys(d).length > 0, {
  message: 'At least one field must be provided for update',
});
export type UpdateMedicalHistoryRequest = z.infer<typeof UpdateMedicalHistoryRequestSchema>;

// ─── List query ────────────────────────────────────────────────────────────────

export const MedicalHistoryListQuerySchema = PaginationQuerySchema.extend({
  hospital_id: uuidSchema,
  is_active:   z.enum(['true', 'false']).optional(),
});
export type MedicalHistoryListQuery = z.infer<typeof MedicalHistoryListQuerySchema>;

// ─── Get-latest query (no pagination) ──────────────────────────────────────────

export const MedicalHistoryLatestQuerySchema = z.object({});
export type MedicalHistoryLatestQuery = z.infer<typeof MedicalHistoryLatestQuerySchema>;

// ─── Response item ─────────────────────────────────────────────────────────────

export const MedicalHistoryItemSchema = z.object({
  history_id:   uuidSchema,
  patient_id:   uuidSchema,
  hospital_id:  uuidSchema,
  encounter_id: uuidSchema.nullable(),
  narrative:    z.string(),
  recorded_by:  z.string().min(1).max(200),
  recorded_by_name: z.string().nullable().optional(),
  recorded_at:  z.coerce.date(),
  is_active:    z.boolean(),
  created_at:   z.coerce.date(),
  updated_at:   z.coerce.date(),
});
export type MedicalHistoryItem = z.infer<typeof MedicalHistoryItemSchema>;

// ─── List response ─────────────────────────────────────────────────────────────

export const GetMedicalHistoryListResponseSchema = z.object({
  items:      z.array(MedicalHistoryItemSchema),
  pagination: PaginationMetaSchema,
});
export type GetMedicalHistoryListResponse = z.infer<typeof GetMedicalHistoryListResponseSchema>;

// ═══════════════════════════════════════════════════════════════════════════════
// PATIENT CURRENT MEDICATIONS
// ═══════════════════════════════════════════════════════════════════════════════

export const MedicationFrequencySchema = z.enum(['DAILY', 'WEEKLY', 'MONTHLY', 'AS_NEEDED']);
export type MedicationFrequency = z.infer<typeof MedicationFrequencySchema>;

// ─── Create request ────────────────────────────────────────────────────────────

export const CreateMedicationRequestSchema = z.object({
  patient_id:      uuidSchema,
  hospital_id:     uuidSchema,
  encounter_id:    uuidSchema.optional(),
  medication_name: z.string().min(1, 'Medication name is required').max(200),
  dose:            z.string().max(100).optional(),
  frequency:       MedicationFrequencySchema,
  start_date:      z.coerce.date().optional(),
  notes:           z.string().max(1000).optional(),
  consent:         z.boolean().default(false),
});
export type CreateMedicationRequest = z.infer<typeof CreateMedicationRequestSchema>;

// ─── Update request ────────────────────────────────────────────────────────────

export const UpdateMedicationRequestSchema = z.object({
  medication_name: z.string().min(1).max(200).optional(),
  // dose / start_date / notes accept `null` (in addition to omission) so the
  // client can explicitly clear a previously-set value — sending `undefined`
  // (i.e. omitting the key) means "leave unchanged", while `null` means
  // "clear this field". See PatientCurrentMedicationsRepo.update().
  dose:            z.string().max(100).nullable().optional(),
  frequency:       MedicationFrequencySchema.optional(),
  start_date:      z.coerce.date().nullable().optional(),
  notes:           z.string().max(1000).nullable().optional(),
  consent:         z.boolean().optional(),
  is_active:       z.boolean().optional(),
}).refine((d) => Object.keys(d).length > 0, {
  message: 'At least one field must be provided for update',
});
export type UpdateMedicationRequest = z.infer<typeof UpdateMedicationRequestSchema>;

// ─── List query ────────────────────────────────────────────────────────────────

export const MedicationListQuerySchema = PaginationQuerySchema.extend({
  is_active:   z.enum(['true', 'false']).optional(),
});
export type MedicationListQuery = z.infer<typeof MedicationListQuerySchema>;

// ─── Response item ─────────────────────────────────────────────────────────────

export const MedicationItemSchema = z.object({
  medication_id:    uuidSchema,
  patient_id:       uuidSchema,
  hospital_id:      uuidSchema,
  encounter_id:     uuidSchema.nullable(),
  medication_name:  z.string(),
  dose:             z.string().nullable(),
  frequency:        z.string(),
  start_date:       z.coerce.date().nullable(),
  notes:            z.string().nullable(),
  recorded_by:      uuidSchema,
  recorded_by_name: z.string().nullable().optional(),
  consent:          z.boolean(),
  is_active:        z.boolean(),
  created_at:       z.coerce.date(),
  updated_at:       z.coerce.date(),
});
export type MedicationItem = z.infer<typeof MedicationItemSchema>;

// ─── List response ─────────────────────────────────────────────────────────────

export const GetMedicationListResponseSchema = z.object({
  items:      z.array(MedicationItemSchema),
  pagination: PaginationMetaSchema,
});
export type GetMedicationListResponse = z.infer<typeof GetMedicationListResponseSchema>;
